import { useState, useEffect } from "react";
import { Moon, Sun, Type, Printer, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface BookReadingControlsProps {
  fontSize: number;
  onFontSizeChange: (size: number) => void;
  darkMode: boolean;
  onDarkModeChange: (enabled: boolean) => void;
}

const BookReadingControls = ({
  fontSize,
  onFontSizeChange,
  darkMode,
  onDarkModeChange,
}: BookReadingControlsProps) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showPanel, setShowPanel] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = window.scrollY;
      const progress = (scrolled / documentHeight) * 100;
      setScrollProgress(Math.min(progress, 100));
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial calculation

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const increaseFontSize = () => {
    if (fontSize < 24) {
      onFontSizeChange(fontSize + 2);
    }
  };

  const decreaseFontSize = () => {
    if (fontSize > 14) {
      onFontSizeChange(fontSize - 2);
    }
  };

  const handlePrintDownload = () => {
    window.print();
  };

  return (
    <>
      {/* Progress Bar */}
      <div className={cn(
        "fixed top-0 left-0 right-0 h-1 z-50",
        darkMode ? "bg-gray-800" : "bg-gray-200"
      )}>
        <div
          className="h-full bg-[#006B5F] transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Settings Toggle Button */}
      <Button
        onClick={() => setShowPanel(!showPanel)}
        className={cn(
          "fixed bottom-4 right-4 z-40 h-auto rounded-lg px-3 py-2 shadow-lg sm:bottom-6 sm:right-6",
          darkMode
            ? "bg-gray-800 hover:bg-gray-700 text-white border border-gray-700"
            : "bg-white hover:bg-gray-50 text-gray-900 border border-gray-200"
        )}
        title="Reading settings"
      >
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4" />
          <span className="text-xs font-medium">Reading</span>
        </div>
      </Button>

      {/* Settings Panel */}
      {showPanel && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/20 z-40"
            onClick={() => setShowPanel(false)}
          />

          {/* Panel */}
          <div className={cn(
            "fixed bottom-20 left-4 right-4 z-50 rounded-lg border p-4 shadow-xl sm:bottom-24 sm:left-auto sm:right-6 sm:w-64",
            darkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          )}>
            <h3 className={cn(
              "text-sm font-semibold mb-4",
              darkMode ? "text-white" : "text-gray-900"
            )}>
              Reading Settings
            </h3>

            {/* Font Size */}
            <div className="mb-4">
              <label className={cn(
                "text-xs font-medium mb-2 block",
                darkMode ? "text-gray-300" : "text-gray-700"
              )}>
                Font Size
              </label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={decreaseFontSize}
                  disabled={fontSize <= 14}
                  className={cn(
                    "h-8 w-8 p-0",
                    darkMode && "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                  )}
                >
                  <div className="flex flex-col items-center justify-center">
                    <Type className="w-3 h-3" />
                    <span className="text-[8px] leading-none mt-0.5">−</span>
                  </div>
                </Button>
                <span className={cn(
                  "text-sm font-medium flex-1 text-center",
                  darkMode ? "text-gray-300" : "text-gray-700"
                )}>
                  {fontSize}px
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={increaseFontSize}
                  disabled={fontSize >= 24}
                  className={cn(
                    "h-8 w-8 p-0",
                    darkMode && "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                  )}
                >
                  <div className="flex flex-col items-center justify-center">
                    <Type className="w-4 h-4" />
                    <span className="text-[8px] leading-none mt-0.5">+</span>
                  </div>
                </Button>
              </div>
            </div>

            {/* Dark Mode */}
            <div className="mb-4">
              <label className={cn(
                "text-xs font-medium mb-2 block",
                darkMode ? "text-gray-300" : "text-gray-700"
              )}>
                Theme
              </label>
              <Button
                variant="outline"
                onClick={() => onDarkModeChange(!darkMode)}
                className={cn(
                  "w-full justify-start gap-2",
                  darkMode && "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                )}
              >
                {darkMode ? (
                  <>
                    <Sun className="w-4 h-4" />
                    <span>Light Mode</span>
                  </>
                ) : (
                  <>
                    <Moon className="w-4 h-4" />
                    <span>Dark Mode</span>
                  </>
                )}
              </Button>
            </div>

            {/* Print */}
            <div>
              <label className={cn(
                "text-xs font-medium mb-2 block",
                darkMode ? "text-gray-300" : "text-gray-700"
              )}>
                Actions
              </label>
              <Button
                variant="outline"
                onClick={handlePrintDownload}
                className={cn(
                  "w-full justify-start gap-2",
                  darkMode && "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                )}
              >
                <Printer className="w-4 h-4" />
                <span>Print / Download</span>
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default BookReadingControls;
